import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TableSortLabel,
  Box,
  Checkbox,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import DeleteConfirmation from "../common/DeleteConfirmation";
import EditOrderReporting from "./EditOrderReporting";
import { NotificationManager } from "react-notifications";
import { DeleteLabOrder, GenerateOrderResultsReport } from "../../services/OrderService";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";
import CommonLoader from '../common/CommonLoader';

const LabOrdersTable = ({ labOrdersData, count, onChange }) => {
  const columns = [
    "Order Number",
    "Client Name",
    "Project Description",
    "Status",
    "Account Number",
    "Date Received",
    "Due Date",
    "Actions",
  ];
  // Kept index-aligned with `columns` (minus the trailing "Actions" cell, which
  // is rendered separately). "Project Description" is stored in project_category.
  const columnHeaders = [
    "orderNumber",
    "clientName",
    "projectCategory",
    "status",
    "accountNumber",
    "dateReceived",
    "reportDueDate",

  ];
  const navigate = useNavigate();
  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedOrderToDelete, setSelectedOrderToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const tableContainerRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(460);
  useEffect(() => {
    handleChange();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop = tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(300, Math.min(availableHeight, windowHeight - 200));
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener('resize', calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [labOrdersData]); // re-runs when data changes

  const dateColumns = ["dateReceived", "reportDueDate"];

  // Report due date is nullable, and legacy rows store 2000-01-01 as the
  // "no date" sentinel. Render both as blank instead of a bogus date.
  const formatDate = (dateString) => {
    if (!dateString || String(dateString).startsWith("2000-01-01")) return "";
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleChange = () => {
    onChange(page, rowsPerPage);
  };

  const handleViewClick = (orderNumber) => {
    const orderUrl = `/vlp/lab-orders/${orderNumber}`;
    if (window.labOrderViewWindow && !window.labOrderViewWindow.closed) {
      window.labOrderViewWindow.location.href = orderUrl;
    } else {
      window.labOrderViewWindow = window.open(
        orderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };

  const handleEditClick = (orderNumber) => {
    const orderUrl = `/vlp/edit/lab-orders/${orderNumber}`;
    if (window.labOrderViewWindow && !window.labOrderViewWindow.closed) {
      window.labOrderViewWindow.location.href = orderUrl;
    } else {
      window.labOrderViewWindow = window.open(
        orderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };

  const handleDeleteClick = (orderNumber) => {
    setSelectedOrderToDelete(orderNumber);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteLabOrder(selectedOrderToDelete);
      if (response.results === true) {
        NotificationManager.success("Lab order deleted successfully");
        setIsDeleteConfirmationOpen(false);
        handleChange();
      } else {
        NotificationManager.error("Error deleting lab order");
      }
    } catch (error) {
      NotificationManager.error("Error deleting lab order");
    }
  };


  //GenerateOrderResultsReport
  const handleGenerateReports = async () => {
    try {
      setLoading(true);
      await GenerateOrderResultsReport(selectedOrders);
      NotificationManager.success("Lab order results reports generated successfully");
      setSelectedOrders([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      NotificationManager.error("Error generating results reports");
    }
  };


  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders((prevSelected) => {
      if (prevSelected.includes(orderId)) {
        return prevSelected.filter((id) => id !== orderId);
      } else {
        return [...prevSelected, orderId];
      }
    });
  };

  const handleUpdateSelected = () => {
    if (selectedOrders.length > 0) {
      const orderToEdit = labOrdersData.find(
        (order) => order.orderNumber === selectedOrders[0]
      );

      if (orderToEdit) {
        const deepCopyOrder = JSON.parse(JSON.stringify(orderToEdit));
        deepCopyOrder.orderNumber = selectedOrders.join(", ");
        setCurrentOrder(deepCopyOrder);
        setIsEditDialogOpen(true);
      }
    }
  };

  const handleDialogClose = () => {
    setIsEditDialogOpen(false);
    setCurrentOrder(null);
  };

  const handleSave = (updatedOrder) => {
    setIsEditDialogOpen(false);
    setCurrentOrder(null);
    setSelectedOrders([]);
  };

  const sortedData = stableSort(labOrdersData, getComparator(order, orderBy));

  const renderActions = (orderId) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(orderId)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(orderId)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(orderId)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      {selectedOrders.length > 0 && (
        <Box sx={{ mb: 2, textAlign: "right", marginRight: "10px" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateSelected}
            sx={{ mr: 1 }}
          >
            Update
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleGenerateReports}
          >
            Results Report
          </Button>
        </Box>
      )}
      <Paper>
        <Box sx={{ width: "99%", overflowX: "auto" }}>
          <TableContainer ref={tableContainerRef} sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}>
            <Table size="small" stickyHeader>
              <TableHead
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: "white",
                }}
              >
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedOrders.length > 0 &&
                        selectedOrders.length < labOrdersData.length
                      }
                      checked={selectedOrders.length === labOrdersData.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOrders(
                            labOrdersData.map((order) => order.orderNumber)
                          );
                        } else {
                          setSelectedOrders([]);
                        }
                      }}
                    />
                  </TableCell>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column}
                      onClick={() => handleRequestSort(columnHeaders[index])}
                    >
                      <TableSortLabel
                        active={orderBy === columnHeaders[index]}
                        direction={
                          orderBy === columnHeaders[index] ? order : "asc"
                        }
                      >
                        {column}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((order, index) => (
                  <TableRow
                    key={order.orderNumber}
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrders.includes(order.orderNumber)}
                        onChange={() => handleCheckboxChange(order.orderNumber)}
                      />
                    </TableCell>

                    {columnHeaders.map((column) => (
                      <TableCell
                        key={column}
                        title={
                          column === "projectCategory"
                            ? order[column] || ""
                            : undefined
                        }
                        sx={
                          column === "projectCategory"
                            ? {
                              maxWidth: 260,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }
                            : undefined
                        }
                      >
                        {dateColumns.includes(column) ? (
                          formatDate(order[column])
                        ) : column === "orderNumber" ? (
                          order[column] ? (
                            <a
                              href={`/vlp/edit/lab-orders/${order[column]}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {order[column]}
                            </a>
                          ) : (
                            "-"
                          )
                        ) : (
                          order[column]
                        )}
                      </TableCell>
                    ))}

                    <TableCell>{renderActions(order.orderNumber)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this lab order?"
        color="red"
      />
      {currentOrder && (
        <EditOrderReporting
          open={isEditDialogOpen}
          order={currentOrder}
          onSave={handleSave}
          onClose={handleDialogClose}
        />
      )}

      <CommonLoader loading={loading} />
    </>
  );
};

export default LabOrdersTable;
